import {
  BulkDeleteButton,
  Create,
  Datagrid,
  Edit,
  List,
  Loading,
  Resource,
  ShowBase,
  SimpleForm,
  TextField,
  TextInput,
  useShowContext,
} from "react-admin";
import { CrudPermissions } from "../auth/crud-permissions";
import { Alert, Box, Typography } from "@mui/material";
import { DashboardResource as DashDTO } from "@arrowsup/platform-dtos";
import { useWindowHeight } from "../../logic/use-window-height";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { TopHelpInfoText } from "../help/top-help-info-text";
import { useBackendSession } from "../../logic/auth/backend-session";
import { usePlatformUiEnv } from "../../logic/env/use-platform-ui-env";
import { PlatformUiEnvToHostMap } from "../../logic/data-provider/platform-ui-env-to-host-map";

export const DashboardResource = (perms: CrudPermissions) => {
  return (
    <Resource
      name="dashboard"
      key="dashboard"
      options={{
        label: "Dashboards",
      }}
      list={DashboardList(perms)}
      edit={perms.update ? DashboardEdit : undefined}
      create={perms.create ? DashboardCreate : undefined}
      icon={DashboardIcon}
      show={DashboardShow}
    />
  );
};

const DashboardList = (perms: CrudPermissions) => (
  <div>
    <TopHelpInfoText text="View analytics dashboards with conversion metrics and performance data." />
    <List exporter={false} sort={{ field: "title", order: "ASC" }} perPage={25}>
      <Datagrid
        rowClick={perms.read ? "show" : false}
        bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
      >
        <TextField source="title" />
      </Datagrid>
    </List>
  </div>
);

const DashboardEdit = () => (
  <Edit redirect="list">
    <SimpleForm>
      <TextField source="title" />
      <TextField source="path" />
    </SimpleForm>
  </Edit>
);

const DashboardCreate = () => (
  <Create redirect="list">
    <SimpleForm>
      <TextInput
        source="title"
        helperText="Title of this dashboard."
        fullWidth
      />
      <TextInput
        source="path"
        helperText="Path of this dashboard. I.e. '/{orgId}/{dashboard-path}/index.html'."
        fullWidth
      />
    </SimpleForm>
  </Create>
);

const DashboardEmbed = () => {
  const showContext = useShowContext();
  const windowHeight = useWindowHeight();
  const env = usePlatformUiEnv();
  // The iframe authenticates with the backend session cookie.
  const session = useBackendSession();

  if (session.error) {
    return (
      <Alert severity="error">
        Unable to load dashboard: {String(session.error)}
      </Alert>
    );
  }

  if (!showContext.record || !session.ready) {
    return <Loading />;
  }

  const record = showContext.record as DashDTO;

  const height = windowHeight - 100; // Approx padding for nav bar.
  const iframeSrc = `${PlatformUiEnvToHostMap[env]}/dashboards${record.path}`;

  return (
    <Box
      display="flex"
      flexDirection={"column"}
      justifyContent="center"
      alignItems="center"
      paddingTop="1rem"
    >
      <Typography variant="h6">{record.title}</Typography>
      <iframe
        title={record.title}
        id="dashboard-iframe"
        src={iframeSrc}
        height={height.toString() + "px"}
        width="100%"
        frameBorder={0}
      ></iframe>
    </Box>
  );
};

const DashboardShow = () => {
  return (
    <ShowBase
      resource="dashboard"
      // Fetch only when uncached, avoiding needless refetches. Never disable
      // the query instead: the list's cached rows expire after a few idle
      // minutes, which would leave a spinner with nothing fetching the record.
      queryOptions={{ staleTime: Infinity }}
    >
      <DashboardEmbed />
    </ShowBase>
  );
};
